import Avatar from "@/components/base/Avatar";
import type { TugJoin } from "@/lib/types";
import { TEAM_SOFT, TEAM_STRIP, TEAM_TEXT, teamKey } from "../utils";

interface Props {
  joins: TugJoin[];
  aName: string;
  bName: string;
  /** 판정에 포함된 인원수. >0 이면 각 팀 상위 n명만 진하게, 나머지는 «미집계» */
  compareN?: number;
}

// 마감된 경기의 팀별 결과 (2열). taps 내림차순 정렬.
// compare_n > 0 이면 각 팀 상위 compare_n 명까지 배경을 진하게 칠하고,
// 그 아래는 흐리게 + «미집계» 를 붙여 어떻게 계산됐는지 한눈에 보이게 한다.
export default function TugResultList({ joins, aName, bName, compareN = 0 }: Props) {
  const a = joins.filter((j) => teamKey(j.team) === "A");
  const b = joins.filter((j) => teamKey(j.team) === "B");
  const best = joins.reduce((m, j) => Math.max(m, j.taps ?? 0), 0);

  if (joins.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-background-300 bg-background-50 px-5 py-8 text-center text-sm text-foreground-500">
        참가자가 없어 겨룰 상대가 없었습니다.
      </p>
    );
  }

  const column = (team: "A" | "B", name: string, list: TugJoin[]) => {
    const sorted = [...list].sort((x, y) => (y.taps ?? 0) - (x.taps ?? 0));
    const countedList = compareN > 0 ? sorted.slice(0, compareN) : sorted;
    const counted = countedList.reduce((sum, j) => sum + (j.taps ?? 0), 0);
    const hasCut = compareN > 0 && sorted.length > compareN;
    return (
      <div className={`overflow-hidden rounded-xl border ${TEAM_SOFT[team]}`}>
        <div className="flex items-center justify-between gap-2 px-4 py-2.5">
          <span className={`font-heading text-sm font-bold ${TEAM_TEXT[team]}`}>
            {name}
          </span>
          <span className="text-right text-sm text-foreground-600">
            {compareN > 0 ? `상위 ${compareN}명 합계 ` : "합계 "}
            <strong className="tabular-nums text-foreground-900">{counted}</strong>
          </span>
        </div>
        {sorted.length === 0 ? (
          <p className="border-t border-background-200 bg-background-50 px-4 py-4 text-center text-xs text-foreground-400">
            참가자 없음
          </p>
        ) : (
          <ul className="divide-y divide-background-200 border-t border-background-200">
            {sorted.map((j, i) => {
              const countedRow = compareN <= 0 || i < compareN;
              return (
                <li
                  key={`${team}-${j.pick}-${j.mem_no}`}
                  className={`flex items-center gap-2 px-3.5 py-2.5 ${
                    countedRow ? TEAM_SOFT[team] : "bg-background-50 text-foreground-400"
                  } ${j.is_mine === 1 ? "ring-2 ring-inset ring-primary-400" : ""}`}
                >
                  <Avatar name={j.name} photo={j.photo} size={24} />
                  <span
                    className={`flex-1 truncate text-sm ${
                      countedRow ? "text-foreground-800" : "text-foreground-400"
                    }`}
                  >
                    <span className="font-medium">
                      {j.aho} {j.name}
                    </span>
                    {j.is_mine === 1 && (
                      <span className="ml-1.5 text-xs font-semibold text-primary-700">
                        나
                      </span>
                    )}
                    {!countedRow && (
                      <span className="ml-1.5 rounded-full bg-background-200 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-500">
                        미집계
                      </span>
                    )}
                  </span>
                  {countedRow && best > 0 && (j.taps ?? 0) === best && (
                    <span className="shrink-0 rounded-full bg-accent-100 px-1.5 py-0.5 text-[11px] font-bold text-accent-900">
                      MVP
                    </span>
                  )}
                  <span
                    className={`shrink-0 font-heading text-sm font-bold tabular-nums ${
                      countedRow ? "text-foreground-950" : "text-foreground-400"
                    }`}
                  >
                    {j.taps}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {hasCut && (
          <p className="border-t border-background-200 bg-background-50 px-4 py-2 text-center text-[11px] text-foreground-400">
            위 {compareN}명만 승부에 집계되었습니다
          </p>
        )}
        <span className={`block h-1 ${TEAM_STRIP[team]}`} aria-hidden="true"></span>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {column("A", aName, a)}
      {column("B", bName, b)}
    </div>
  );
}